/**
 * News · Artículos — el viewer/QA de la clasificación.
 *
 * Tabla paginada con filtros (cefr, categoría IPTC, sensibilidad, fuente,
 * estado, rango de fechas, búsqueda). Cada fila muestra los tags clave
 * (cefr / iptc_category / sensitivity / status) para escanear la calidad de
 * la clasificación del LLM a ojo, y linkea al detalle (QA loop).
 *
 * Los filtros + la página se persisten en la URL (F5 / share-link friendly).
 */
import { useMemo, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchNewsArticles,
  fetchNewsSources,
  NEWS_CEFR_VALUES,
  NEWS_IPTC_CATEGORIES,
  NEWS_SENSITIVITY_VALUES,
  NEWS_STATUS_VALUES,
  type NewsArticleFilters,
} from '../../api/admin/news.js';
import { useUrlParam } from '../../lib/useUrlTab.js';
import { formatDateTime } from '../../lib/format.js';
import { CefrBadge, SensitivityBadge, StatusBadge } from './badges.js';

const PAGE_SIZE = 25;

export function ArticlesListPage(): ReactElement {
  const navigate = useNavigate();
  const [cefr, setCefr] = useUrlParam('cefr');
  const [category, setCategory] = useUrlParam('category');
  const [sensitivity, setSensitivity] = useUrlParam('sensitivity');
  const [source, setSource] = useUrlParam('source');
  const [status, setStatus] = useUrlParam('status');
  const [from, setFrom] = useUrlParam('from');
  const [to, setTo] = useUrlParam('to');
  const [q, setQ] = useUrlParam('q');
  const [pageParam, setPageParam] = useUrlParam('page');
  const page = Math.max(1, Number.parseInt(pageParam ?? '1', 10) || 1);

  // Fuentes para el dropdown de filtro (id → nombre). No bloquea el listado.
  const sourcesQuery = useQuery({
    queryKey: ['admin', 'news', 'sources'],
    queryFn: fetchNewsSources,
  });

  const filters = useMemo<NewsArticleFilters>(() => {
    const f: NewsArticleFilters = { page, pageSize: PAGE_SIZE };
    if (cefr) f.cefr = cefr;
    if (category) f.category = category;
    if (sensitivity) f.sensitivity = sensitivity;
    if (source) f.source = source;
    if (status) f.status = status;
    if (from) f.from = from;
    if (to) f.to = to;
    if (q) f.q = q;
    return f;
  }, [cefr, category, sensitivity, source, status, from, to, q, page]);

  const query = useQuery({
    queryKey: ['admin', 'news', 'articles', filters],
    queryFn: () => fetchNewsArticles(filters),
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total;
  const totalPages = total !== undefined ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : undefined;
  const hasNext = totalPages !== undefined ? page < totalPages : items.length === PAGE_SIZE;

  // Cambiar cualquier filtro resetea la página a 1.
  const onFilter = (setter: (v: string | null) => void) => (v: string | null): void => {
    setter(v);
    setPageParam(null);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">News · Artículos</h1>
          <p className="page-sub">
            Viewer de QA de la clasificación. Filtrá y abrí el detalle para revisar los tags del
            LLM.
            {total !== undefined ? (
              <>
                {' '}·{' '}
                <span className="tt-tert mono">{total.toLocaleString('es-AR')} resultados</span>
              </>
            ) : null}
          </p>
        </div>
      </div>

      <div className="filterbar">
        <label className="group">
          <span>buscar</span>
          <input
            type="text"
            value={q ?? ''}
            placeholder="título o excerpt"
            onChange={(e) => onFilter(setQ)(e.target.value || null)}
            data-testid="news-filter-q"
          />
        </label>
        <label className="group">
          <span>nivel</span>
          <select value={cefr ?? ''} onChange={(e) => onFilter(setCefr)(e.target.value || null)}>
            <option value="">Todos</option>
            {NEWS_CEFR_VALUES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="group">
          <span>categoría</span>
          <select
            value={category ?? ''}
            onChange={(e) => onFilter(setCategory)(e.target.value || null)}
          >
            <option value="">Todas</option>
            {NEWS_IPTC_CATEGORIES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="group">
          <span>sensibilidad</span>
          <select
            value={sensitivity ?? ''}
            onChange={(e) => onFilter(setSensitivity)(e.target.value || null)}
          >
            <option value="">Todas</option>
            {NEWS_SENSITIVITY_VALUES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="group">
          <span>fuente</span>
          <select value={source ?? ''} onChange={(e) => onFilter(setSource)(e.target.value || null)}>
            <option value="">Todas</option>
            {(sourcesQuery.data?.sources ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label className="group">
          <span>estado</span>
          <select value={status ?? ''} onChange={(e) => onFilter(setStatus)(e.target.value || null)}>
            <option value="">Todos</option>
            {NEWS_STATUS_VALUES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="group">
          <span>desde</span>
          <input
            type="date"
            value={from ?? ''}
            onChange={(e) => onFilter(setFrom)(e.target.value || null)}
          />
        </label>
        <label className="group">
          <span>hasta</span>
          <input
            type="date"
            value={to ?? ''}
            onChange={(e) => onFilter(setTo)(e.target.value || null)}
          />
        </label>
      </div>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <>
          <section className="card">
            <table className="data-table" data-testid="news-articles-table">
              <thead>
                <tr>
                  <th>Artículo</th>
                  <th>Fuente</th>
                  <th>Categoría</th>
                  <th>Nivel</th>
                  <th>Sensib.</th>
                  <th>Estado</th>
                  <th>Publicado</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty">Sin artículos para estos filtros</td>
                  </tr>
                ) : (
                  items.map((a) => (
                    <tr
                      key={a.id}
                      className="row"
                      style={{ cursor: 'pointer' }}
                      onClick={() => void navigate(`/news/${a.id}`)}
                      data-testid={`news-article-row-${a.id}`}
                    >
                      <td>
                        <Link
                          to={`/news/${a.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="news-title-link"
                        >
                          {a.title}
                        </Link>
                        {a.classifiedAt === null ? (
                          <span className="badge b-warn" style={{ marginLeft: 6 }}>
                            sin clasificar
                          </span>
                        ) : null}
                      </td>
                      <td className="tt-sec">{a.sourceName}</td>
                      <td className="tt-sec">{a.iptcCategory ?? <span className="muted">—</span>}</td>
                      <td><CefrBadge value={a.cefr} /></td>
                      <td><SensitivityBadge value={a.sensitivity} /></td>
                      <td><StatusBadge value={a.status} /></td>
                      <td className="tt-tert">{formatDateTime(a.publishedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <div className="flex ai-c gap-4 pad-6">
            <button
              type="button"
              className="btn"
              disabled={page <= 1}
              onClick={() => setPageParam(page - 1 <= 1 ? null : String(page - 1))}
            >
              ← Anterior
            </button>
            <span className="tt-tert mono">
              Página {page}
              {totalPages !== undefined ? ` / ${totalPages}` : ''}
            </span>
            <button
              type="button"
              className="btn"
              disabled={!hasNext}
              onClick={() => setPageParam(String(page + 1))}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
    </>
  );
}
